import { qrSvg } from '@mostajs/qrpanel';
import axios from 'axios'; 

const API_URL = '/api/printers';

/**
 * Service pour gérer les opérations liées aux imprimantes
 */
 
/**
 * Service pour gérer les fonctionnalités d'impression
 */
const printerService = {


    /**
   * Récupérer la liste des imprimantes connectées
   * @returns {Promise} Promesse résolue avec la liste des imprimantes
   */
  getPrinters: async () => {
    try {
      const response = await axios.get(`${API_URL}/list`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des imprimantes:', error);
      throw error;
    }
  },

  /**
   * Récupérer la configuration de l'imprimante
   * @returns {Promise} Promesse résolue avec la configuration de l'imprimante
   */
  getConfig: async () => {
    try {
      const response = await axios.get(`${API_URL}/config`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de la configuration:', error);
      throw error;
    }
  },

  /**
   * Mettre à jour la configuration de l'imprimante
   * @param {Object} config - Nouvelle configuration
   * @returns {Promise} Promesse résolue avec la configuration mise à jour
   */
  updateConfig: async (config) => {
    try {
      const response = await axios.post(`${API_URL}/config`, config);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la configuration:', error);
      throw error;
    }
  },

  /**
   * Effectuer un test d'impression
   * @param {Object} config - Configuration pour le test
   * @returns {Promise} Promesse résolue avec le statut du test
   */
  testPrint: async (config) => {
    try {
      const response = await axios.post(`${API_URL}/test`, config);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du test d\'impression:', error);
      throw error;
    }
  },

  /**
   * Imprimer un ticket
   * @param {string} ticketId - ID du ticket à imprimer
   * @param {string} serviceType - Type de service du ticket
   * @param {Object} config - Configuration d'impression (optionnel)
   * @returns {Promise} Promesse résolue avec le statut de l'impression
   */
  printTicket: async (ticketId, serviceType, config = null) => {
    try {
      const response = await axios.post(`${API_URL}/print`, {
        ticketId,
        serviceType,
        printerConfig: config
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'impression du ticket:', error);
      throw error;
    }
  },



  /**
   * Imprimer un ticket avec les paramètres configurés
   * @param {Object} ticket - Données du ticket à imprimer
   * @param {Object} config - Configuration de l'imprimante
   * @returns {Promise<boolean>} - Promesse indiquant si l'impression a réussi
   */
  printAPITicket: async (ticket, config) => {
    // Si la configuration spécifie d'utiliser l'API serveur, essayez d'abord cette méthode
    if (config.useServerApi) {
      try {
        const response = await axios.post('/api/print/ticket', { ticket, config });
        return response.data.success;
      } catch (error) {
        console.error('Erreur lors de l\'impression via l\'API:', error);
        // Si l'API échoue, essayez l'impression côté client
      }
    }

    // Méthode d'impression côté client
    return printerService.printTicketClient(ticket, config);
  },

  /**
   * Imprimer un ticket côté client
   * @param {Object} ticket - Données du ticket à imprimer
   * @param {Object} config - Configuration de l'imprimante
   * @returns {Promise<boolean>} - Promesse indiquant si l'impression a réussi
   */
  printTicketClient: (ticket, config) => {
    return new Promise((resolve, reject) => {
      try {
        // Créer le contenu du ticket
        const ticketContent = printerService.generateTicketHTML(ticket, config);
        
        // Créer un iframe pour l'impression
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        
        // Ajouter l'iframe au document
        document.body.appendChild(iframe);
        
        // Écrire le contenu dans l'iframe
        iframe.contentDocument.write(ticketContent);
        
        // Fonction pour imprimer lorsque l'iframe est chargé
        iframe.onload = () => {
          try {
            setTimeout(() => {
              iframe.contentWindow.focus();
              
              // Si l'impression automatique est activée et le navigateur le permet
              if (config.autoprint) {
                try {
                  // Tentative d'impression silencieuse si disponible (Chrome avec options d'impression avancées)
                  if (iframe.contentWindow.print && typeof iframe.contentWindow.print === 'function') {
                    iframe.contentWindow.print({
                      silent: true,
                      copies: config.copies || 1
                    });
                  } else {
                    // Méthode standard
                    iframe.contentWindow.print();
                  }
                } catch (e) {
                  // Fallback si l'impression silencieuse échoue
                  iframe.contentWindow.print();
                }
              } else {
                // Impression standard
                iframe.contentWindow.print();
              }
              
              // Supprimer l'iframe après l'impression
              setTimeout(() => {
                document.body.removeChild(iframe);
                resolve(true);
              }, 1000);
            }, 500);
          } catch (error) {
            console.error('Erreur lors de l\'impression:', error);
            document.body.removeChild(iframe);
            reject(error);
          }
        };
        
        // En cas d'erreur lors du chargement
        iframe.onerror = (error) => {
          document.body.removeChild(iframe);
          reject(error);
        };
      } catch (error) {
        console.error('Erreur lors de la préparation de l\'impression:', error);
        reject(error);
      }
    });
  },

  /**
   * Générer le HTML du ticket pour l'impression
   * @param {Object} ticket - Données du ticket à imprimer
   * @param {Object} config - Configuration de l'imprimante
   * @returns {string} - HTML du ticket
   */
  generateTicketHTML: (ticket, config) => {
    // Configuration des options d'impression CSS
    const paperSize = config.paperSize === 'custom' 
      ? `${config.customWidth}mm ${config.customHeight}mm` 
      : config.paperSize;
    
    const margins = config.margins || { top: 5, right: 5, bottom: 5, left: 5 };
    
    // Styles CSS pour le ticket
    const ticketStyle = `
      @page {
        size: ${paperSize} ${config.orientation};
        margin: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
      }
      body {
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 0;
        background-color: white;
      }
      .ticket-container {
        width: 100%;
        text-align: center;
      }
      .ticket-logo {
        max-width: 80%;
        height: auto;
        margin: 0 auto;
        display: ${config.showLogo ? 'block' : 'none'};
      }
      .ticket-agency {
        font-size: 14pt;
        font-weight: bold;
        margin-top: 10px;
        margin-bottom: 5px;
      }
      .ticket-company {
        font-size: 10pt;
        margin-bottom: 15px;
      }
      .ticket-number {
        font-size: 28pt;
        font-weight: bold;
        margin: 10px 0;
      }
      .ticket-service {
        font-size: 12pt;
        margin: 5px 0;
      }
      .ticket-date {
        font-size: 10pt;
        margin: 10px 0;
        display: ${config.showDate ? 'block' : 'none'};
      }
      .ticket-qrcode {
        margin: 10px auto;
        display: ${config.showQRCode ? 'flex' : 'none'};
        align-items: center;
        justify-content: center;
      }
      /* Le QR est un SVG : on lui impose une taille papier, sans fond gris — un aplat
         derrière un code-barres nuit à la lecture par un téléphone. */
      .ticket-qrcode svg { width: 100px; height: 100px; }
      .ticket-footer {
        font-size: 8pt;
        margin-top: 15px;
        padding-top: 5px;
        border-top: 1px dashed #ccc;
      }
    `;

    /**
     * QR du ticket — l'URL de SUIVI si le site l'a configurée, le numéro sinon.
     *
     * Cette fonction dessinait `[Code QR]` en toutes lettres : le réglage « Imprimer le
     * QR code » de la console était donc coché depuis toujours pour un résultat qui
     * n'était pas un QR. Un client qui scannait ne trouvait rien à scanner.
     *
     * L'encodage est fait ICI, dans le navigateur : `@mostajs/qrpanel` est pur JS et sans
     * dépendance, ce qui évite un aller-retour au serveur au moment précis où le client
     * attend son papier devant la borne.
     */
    const qrDuTicket = (t) => {
      const suivi = t.ticketSuivi;
      const texte = suivi && suivi.url && suivi.siteId && t.ticketId
        ? `${String(suivi.url).replace(/\/+$/, '')}/t/${encodeURIComponent(suivi.siteId)}`
          + `/${encodeURIComponent(t.serviceType || '')}/${encodeURIComponent(t.ticketId)}`
        : String(t.id || t.ticketId || '');
      if (!texte) return '';
      try {
        return qrSvg(texte, { scale: 3, margin: 2 });
      } catch (e) {
        // Un QR qui refuse de s'encoder ne doit pas empêcher le ticket de sortir : le
        // client attend son papier, pas un carré noir et blanc.
        console.warn('QR non généré :', e && e.message);
        return '';
      }
    };

    // Création du contenu HTML
    const currentDate = new Date(ticket.timestamp || new Date()).toLocaleString();
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Impression de Ticket</title>
          <meta charset="UTF-8">
          <style>${ticketStyle}</style>
        </head>
        <body>
          <div class="ticket-container">
            ${config.showLogo ? `<img src="${ticket.logo || '/logo.png'}" alt="Logo" class="ticket-logo" />` : ''}
            <div class="ticket-agency">${ticket.agencyName || 'Agence'}</div>
            <div class="ticket-company">${ticket.companyName || ''}</div>
            <div class="ticket-number">${ticket.id}</div>
            <div class="ticket-service">${ticket.serviceName}</div>
            ${ticket.serviceNameAr ? `<div class="ticket-service" dir="rtl">${ticket.serviceNameAr}</div>` : ''}
            ${config.showDate ? `<div class="ticket-date">${currentDate}</div>` : ''}
            ${config.showQRCode ? `<div class="ticket-qrcode">${qrDuTicket(ticket)}</div>` : ''}
            <div class="ticket-footer">${config.additionalText || 'Merci de votre visite'}</div>
          </div>
          <script>
            // Script pour déclencher l'impression automatiquement
            window.onload = function() {
              // Ce script est juste pour indiquer que la page est chargée
              console.log('Ticket prêt pour impression');
            };
          </script>
        </body>
      </html>
    `;
  }
};

export default printerService;